import { useEffect, useState } from 'react';
import { apiMessage } from '../api/errors.ts';
import { api } from '../api/instance.ts';
import type { Schemas } from '../api/types.ts';
import { toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { Section } from '../ui/Section.tsx';

/** Usage dashboard (`/v1/orgs/{id}/usage`) and the admin daily credit cap. */
export function Usage({ orgId, canEdit, cap }: { orgId: string; canEdit: boolean; cap: number | null | undefined }) {
  const [usage, setUsage] = useState<Schemas['OrgUsage'] | null>(null);
  const [capText, setCapText] = useState(cap == null ? '' : String(cap));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .orgUsage(orgId)
      .then(setUsage)
      .catch((e) => toast(apiMessage(e, 'Could not load usage.'), 'error'));
  }, [orgId]);

  const max = Math.max(1, ...(usage?.monthToDate.members.map((m) => m.credits) ?? [1]));
  return (
    <div className="flex flex-col gap-6">
      <Section title="Today">
        <div className="flex flex-wrap gap-3">
          <div className="flex min-w-[160px] flex-col rounded-md bg-surface p-4">
            <span className="t-display tabular" data-testid="usage-today">
              {usage?.today.spent ?? '–'}
            </span>
            <span className="t-caption text-dim">
              credits used today{usage?.today.cap != null ? ` of ${usage.today.cap}` : ''}
            </span>
          </div>
          <div className="flex min-w-[160px] flex-col rounded-md bg-surface p-4">
            <span className="t-display tabular">{usage?.monthToDate.total ?? '–'}</span>
            <span className="t-caption text-dim">credits this month</span>
          </div>
        </div>
      </Section>
      <Section title="By member (this month)">
        <div className="flex flex-col gap-2">
          {(usage?.monthToDate.members ?? []).map((m) => (
            <div key={m.userId} className="flex flex-col gap-1">
              <div className="flex justify-between">
                <span className="t-label truncate">{m.email ?? m.userId}</span>
                <span className="t-caption tabular text-dim">
                  {m.renders} renders · {m.credits} credits
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-surface3">
                <div className="h-full rounded-full bg-accent" style={{ width: `${(m.credits / max) * 100}%` }} />
              </div>
            </div>
          ))}
          {usage && usage.monthToDate.members.length === 0 ? (
            <p className="t-caption text-dim">No renders yet this month.</p>
          ) : null}
        </div>
      </Section>
      <Section title="Daily limit">
        <p className="t-caption text-dim">
          Cap the AI credits the whole company can use per day. Leave empty for the default; 0 turns AI renders off.
        </p>
        <form
          className="flex items-end gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              const v = capText.trim() === '' ? null : Math.max(0, Number.parseInt(capText, 10));
              await api.updateOrgSettings(orgId, Number.isFinite(v as number) || v === null ? v : null);
              toast('Daily limit saved', 'success');
            } catch (err) {
              toast(apiMessage(err, 'Could not save the limit.'), 'error');
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field
            label="Credits per day"
            className="w-44"
            inputMode="numeric"
            disabled={!canEdit}
            value={capText}
            placeholder="default"
            onChange={(e) => setCapText(e.target.value.replace(/\D/g, ''))}
          />
          {canEdit ? (
            <Button type="submit" loading={busy}>
              Save
            </Button>
          ) : null}
        </form>
      </Section>
    </div>
  );
}
